import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Container,
  Stack,
  useTheme,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Logo from './Logo';
import { ThemeToggle } from '../design-system/components/ThemeToggle';

// Use Material Symbols for consistency
const MaterialSymbol = ({ iconName, sx, weight = 300 }) => (
  <span
    className="material-symbols-outlined"
    style={{
      fontVariationSettings: `'FILL' 0, 'wght' ${weight}, 'GRAD' 0, 'opsz' 24`, // Adjust opsz if needed
      fontSize: sx?.fontSize || '1.5rem', // Default size
      lineHeight: 1,
      color: sx?.color || 'inherit',
      ...sx,
    }}
  >
    {iconName}
  </span>
);

function Navigation() {
  const location = useLocation();
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [coursesMenuOpen, setCoursesMenuOpen] = useState(false);
  const anchorRef = React.useRef(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCoursesMenuToggle = () => {
    setCoursesMenuOpen((prevOpen) => !prevOpen);
  };

  const handleCoursesMenuClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setCoursesMenuOpen(false);
  };

  // Available courses data
  const availableCourses = [
    { 
      title: "BLS/CPR Certification", 
      path: "/courses/bls-cpr"
    },
    { 
      title: "Fall Prevention Protocol", 
      path: "/courses/fall-prevention"
    },
    { 
      title: "Choking Management Protocol", 
      path: "/courses/choking-management"
    }
  ];

  const navItems = [
    { path: '/', label: 'Home', iconName: 'home' },
    { 
      path: '/courses', 
      label: 'Courses', 
      iconName: 'school',
      hasDropdown: true,
    },
    { path: '/dashboard', label: 'Dashboard', iconName: 'dashboard' },
    { path: '/community', label: 'Community', iconName: 'groups' },
  ];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', width: 250 }}>
      <Box sx={{ my: 2 }}>
        <Logo />
      </Box>
      <Divider />
      <List>
        {navItems.map((item) => (
          <React.Fragment key={item.path}>
            <ListItem disablePadding>
              <ListItemButton 
                component={RouterLink} 
                to={item.path} 
                selected={location.pathname === item.path}
                sx={{ textAlign: 'left', pl: 3 }}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
            {item.hasDropdown && (
              <List component="div" disablePadding>
                {availableCourses.map((course) => (
                  <ListItemButton
                    key={course.path}
                    component={RouterLink}
                    to={course.path}
                    selected={location.pathname === course.path}
                    sx={{ pl: 6 }}
                  >
                    <ListItemText primary={course.title} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <AppBar 
      position="sticky" 
      elevation={0}
      sx={{
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(15, 23, 42, 0.8)' // Semi-transparent dark background
          : 'rgba(255, 255, 255, 0.8)', // Semi-transparent light background
        backdropFilter: 'blur(8px)', // Add blur effect for modern look
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          {/* Logo */}
          <RouterLink to="/" style={{ textDecoration: 'none' }}>
            <Logo />
          </RouterLink>

          {/* Desktop Navigation Links */}
          <Stack 
            direction="row" 
            spacing={1}
            alignItems="center" 
            sx={{ display: { xs: 'none', md: 'flex' } }}
          >
            {navItems.map((item) => (
              item.hasDropdown ? (
                <Box key={item.path}>
                  <Button
                    ref={anchorRef}
                    aria-controls={coursesMenuOpen ? 'courses-menu' : undefined}
                    aria-expanded={coursesMenuOpen ? 'true' : undefined}
                    aria-haspopup="true"
                    onClick={handleCoursesMenuToggle}
                    endIcon={<ArrowDropDownIcon />}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      fontWeight: location.pathname.includes('/courses') ? 600 : 400,
                      color: location.pathname.includes('/courses') 
                        ? 'primary.main' 
                        : theme.palette.mode === 'dark' ? 'grey.300' : 'text.primary',
                      borderRadius: '8px',
                      px: 1.5,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                  <Popper
                    open={coursesMenuOpen}
                    anchorEl={anchorRef.current}
                    role={undefined}
                    placement="bottom-start"
                    transition
                    disablePortal
                    style={{ zIndex: 1300 }}
                  >
                    {({ TransitionProps, placement }) => (
                      <Grow
                        {...TransitionProps}
                        style={{
                          transformOrigin:
                            placement === 'bottom-start' ? 'left top' : 'left bottom',
                        }}
                      >
                        <Paper elevation={3} sx={{ mt: 1, borderRadius: 2, overflow: 'hidden' }}>
                          <ClickAwayListener onClickAway={handleCoursesMenuClose}>
                            <MenuList
                              autoFocusItem={coursesMenuOpen}
                              id="courses-menu"
                              aria-labelledby="courses-button"
                            >
                              <MenuItem
                                component={RouterLink}
                                to="/courses"
                                onClick={handleCoursesMenuClose}
                                sx={{ 
                                  py: 1.5, 
                                  fontWeight: location.pathname === '/courses' ? 600 : 400,
                                  color: location.pathname === '/courses' ? 'primary.main' : 'inherit'
                                }}
                              >
                                All Courses
                              </MenuItem>
                              <Divider />
                              {availableCourses.map((course) => (
                                <MenuItem
                                  key={course.path}
                                  component={RouterLink}
                                  to={course.path}
                                  onClick={handleCoursesMenuClose}
                                  sx={{ 
                                    py: 1.5, 
                                    fontWeight: location.pathname === course.path ? 600 : 400,
                                    color: location.pathname === course.path ? 'primary.main' : 'inherit'
                                  }}
                                >
                                  {course.title}
                                </MenuItem>
                              ))}
                            </MenuList>
                          </ClickAwayListener>
                        </Paper>
                      </Grow>
                    )}
                  </Popper>
                </Box>
              ) : (
                <Button
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  color={location.pathname === item.path ? 'primary' : 'inherit'}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    color: location.pathname === item.path 
                      ? 'primary.main' 
                      : theme.palette.mode === 'dark' ? 'grey.300' : 'text.primary',
                    borderRadius: '8px',
                    px: 1.5,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              )
            ))}
            <ThemeToggle />
          </Stack>

          {/* Mobile Right Icons (Menu + Theme Toggle) */}
          <Stack 
            direction="row" 
            alignItems="center" 
            spacing={0.5} // Small spacing between icons
            sx={{ display: { xs: 'flex', md: 'none' } }} // Show only on mobile
          >
            <ThemeToggle /> 
            <IconButton
              // Use theme text color for visibility
              sx={{ 
                color: theme.palette.mode === 'dark' ? 'grey.300' : 'text.primary',
                padding: 1, // Add explicit padding (theme.spacing(1) = 8px)
              }} 
              aria-label="open drawer"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          </Stack>
        </Toolbar>
      </Container>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
}

export default Navigation;
